import React, { Fragment, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";

import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsIcon from "@mui/icons-material/Settings";
import ReplyIcon from "@mui/icons-material/Reply";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";

import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import MediationRoundedIcon from "@mui/icons-material/MediationRounded";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BrowserUpdatedOutlinedIcon from "@mui/icons-material/BrowserUpdatedOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import AddchartOutlinedIcon from "@mui/icons-material/AddchartOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";

import AppsIcon from "@mui/icons-material/Apps";

import { OverviewCardContainer } from "./card-container";
import { navManageSchemas, navImportWorkspace, navExportWorkspace } from "../../kmk/AppData";
import useWindowResize from "../../../hooks/useWindowsReszie";

const useStyles = makeStyles()((theme) => ({
  svg: {
    "& .MuiSvgIcon-root": {
      color: "#0AB2B2",
    },
  },
  label: {
    fontSize: "0.775rem",
  },
  rotate180: {
    transform: "scaleX(-1)",
  },
  menuItem: {
    padding: "10px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "16px",
    "&:hover": {
      WebkitTextDecorationColor: "none",
      textDecoration: "none",
      backgroundColor: "rgba(17, 25, 39, 0.04)",
    },
  },
  menuItemLable: {
    maxWidth: "200px",
    minWidth: "130px",
  },
  menuItemIconContainer: {
    height: "35px",
    marginRight: "5px",
    "& .MuiSvgIcon-root": {
      color: "#0AB2B2",
    },
  },
  menuItemTextContainer: {
    display: "block",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
}));

export const LinksRow = (props) => {
  const { containerStyle, sx, setOpenSettings, setOpenSharedWorkspace } = props;
  const { classes } = useStyles();
  const user = useSelector((state) => state.user);
  const { open = false } = useSelector((state) => state?.sideNavbar);
  const {
    workspace,
    Owner,
    Owner_id,
    quickImportAllowed = false,
    connections = [],
  } = useSelector((state) => state.workspace);

  const { Workspace_id } = useParams();
  const { accessRole } = workspace;

  const owner = useMemo(() => ({ User_id: Owner_id, UserName: Owner }), [Owner, Owner_id]);
  const $sageMakerAllowed = useMemo(
    () => connections.find((connection) => connection.Type === "SageMaker") || false,
    [connections]
  );

  const boxRef = useRef(null);
  const { width: boxWidth } = useWindowResize(0, 0, boxRef);
  const wholeElemIndex = useMemo(() => (boxWidth - (open ? 220 : 0) + 60) / 160, [boxWidth, open]); // 220 for drawer adjustment, 60 is for three dot icon adjustment, 160 is taken as avg width of menu item

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  }, []);

  const MAIN_OPTIONS = useMemo(() => {
    const options = [
      {
        title: "Datasets",
        icon: <SourceOutlinedIcon fontSize="large" color="info" />,
        id: "datasets",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/data-sources/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "Entities",
        icon: <MediationRoundedIcon fontSize="large" color="info" />,
        id: "entities",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/entities/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "Data Pipelines",
        icon: <AccountTreeOutlinedIcon fontSize="large" color="info" />,
        id: "datapipelines",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/scheduled-jobs/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "SQL Explorer",
        icon: <TableChartOutlinedIcon fontSize="large" color="info" />,
        id: "dataexplorer",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/data-explorer/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "Quick Import",
        icon: <BrowserUpdatedOutlinedIcon fontSize="large" color="info" />,
        id: "quickimport",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/load-data/`),
        admin: true,
        member: true,
        visitor: quickImportAllowed,
      },
      {
        title: "Data AI",
        icon: <AutoAwesomeIcon fontSize="large" color="info" />,
        id: "dataai",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/data-ai/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "Machine Learning",
        icon: <TipsAndUpdatesOutlinedIcon fontSize="large" color="info" />,
        id: "machinelearning",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/machine-learning/`),
        admin: $sageMakerAllowed,
        member: $sageMakerAllowed,
        visitor: $sageMakerAllowed,
      },
      {
        title: "Data Connections",
        icon: <AddchartOutlinedIcon fontSize="large" color="info" />,
        id: "dataconnections",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/data-connections/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "App Hosting",
        icon: <CloudDoneOutlinedIcon fontSize="large" color="info" />,
        id: "apphosting",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/app-hosting/`),
        admin: true,
        member: true,
        visitor: true,
      },
      {
        title: "General Settings",
        icon: <SettingsIcon fontSize="large" color="info" />,
        id: "generalSettings",
        handleClick: () => setOpenSettings(true),
        admin: true,
      },
      {
        title: "Manage Schemas",
        icon: <AdminPanelSettingsIcon fontSize="large" color="info" />,
        id: "manageSchemas",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/${navManageSchemas.link}`),
        admin: true,
        member: true,
      },
      {
        title: "Import Workspace Configuration",
        icon: <DownloadIcon fontSize="large" color="info" />,
        id: "importWSConfig",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/${navImportWorkspace.link}`),
        admin: true,
      },
      {
        title: "Export Workspace Configuration",
        icon: <UploadIcon fontSize="large" color="info" />,
        id: "exportWSConfig",
        handleClick: () => navigate(`/my-workspaces/${Workspace_id}/${navExportWorkspace.link}`),
        admin: true,
      },
      {
        title: "Share",
        icon: <ReplyIcon className={classes.rotate180} fontSize="large" color="info" />,
        id: "Share",
        handleClick: () => setOpenSharedWorkspace(true),
        admin: true,
      },
    ];

    return options;
  }, [
    $sageMakerAllowed,
    Workspace_id,
    classes.rotate180,
    navigate,
    quickImportAllowed,
    setOpenSettings,
    setOpenSharedWorkspace,
  ]);

  const $menuOptions = useMemo(() => {
    const filteredOptions = MAIN_OPTIONS?.filter(({ id, admin, member, visitor }) => {
      if (id === "Share" && user.currentUser.User_id === owner.User_id) return true;

      if (accessRole === 1) return admin || false;
      if (accessRole === 2) return member || false;
      return visitor || false;
    });

    filteredOptions.splice(wholeElemIndex - 1, 0, {
      title: "3 Dots",
      icon: <MoreHorizOutlinedIcon fontSize="large" color="info" />,
      id: "3dots",
      handleClick: handleMenuOpen,
    });

    return filteredOptions;
  }, [wholeElemIndex, MAIN_OPTIONS, accessRole, owner.User_id, user.currentUser.User_id, handleMenuOpen]);

  const handleClose = () => {
    setAnchorEl(null);
    setIsMenuOpen(false);
  };

  return (
    <>
      {$menuOptions.length > 0 ? (
        <OverviewCardContainer sx={sx} containerStyle={containerStyle}>
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", overflowX: "hidden" }}
            ref={boxRef}
          >
            {$menuOptions.slice(0, wholeElemIndex).map(({ title, id, handleClick, icon }, index) => {
              if (id === "3dots") {
                if (index >= $menuOptions?.length - 1) return <></>;
                return (
                  <Fragment key={id}>
                    <IconButton onClick={handleClick} className={classes.menuItem}>
                      {icon}
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={isMenuOpen}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {$menuOptions.slice(wholeElemIndex).map(({ title, id, handleClick, icon }) => (
                        <MenuItem
                          key={id}
                          onClick={() => {
                            handleClick();
                            handleClose();
                          }}
                          className={classes.svg}
                          sx={{ padding: "8px 16px" }}
                        >
                          {icon}
                          <Typography variant="caption" className={classes.label} sx={{ marginLeft: "8px" }}>
                            {title}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Fragment>
                );
              } else
                return (
                  <Box key={id} className={`${classes.menuItem} ${classes.menuItemLable}`} onClick={handleClick}>
                    <Box className={classes.menuItemIconContainer}>{icon}</Box>
                    <Typography
                      variant="caption"
                      className={`${classes.menuItemTextContainer} ${classes.label}`}
                      fontWeight={600}
                    >
                      {title}
                    </Typography>
                  </Box>
                );
            })}
          </Box>
          <Box sx={{ color: "grey", p: 1, textAlign: "center" }} fontSize={"0.875rem"}>
            You can access these features from the '
            <AppsIcon sx={{ width: 24, height: 24, verticalAlign: "middle" }} color="grey" />
            workspace menu' at any time, on any of your workspace pages.
          </Box>
        </OverviewCardContainer>
      ) : (
        ""
      )}
    </>
  );
};

LinksRow.propTypes = {
  sx: PropTypes.object,
  containerStyle: PropTypes.object,
  setOpenSettings: PropTypes.func,
  setOpenSharedWorkspace: PropTypes.func,
};
